import { FC, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Loader from '../../components/loader/index';
import { useDataRoomInvite } from '../../hooks/space/index';
import { accessTokenStorage } from '../../storage/auth/access-token-storage.util';
import { dataRoomInviteStore } from '../../storage/local/index';
import { PageRoute } from '../../types/page.types';
import { refusalCopyOf } from './data-room-invite-refusal';

const Card: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
    <div className="flex w-full max-w-[480px] flex-col gap-4 rounded-lg border border-gray-100 bg-white p-8">
      {children}
    </div>
  </div>
);

export const DataRoomInvitePage: FC = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { error, invite, isLoading } = useDataRoomInvite(token);

  /**
   * Straight on to the one page this address can use, with no step of its own in between.
   *
   * The link's whole job is to reach Log in for an account that exists and Sign up for one that
   * does not, and only the server knows which — so this page asks, and then gets out of the way.
   * A card with a button said nothing the next page does not say better, and 20 asks for the
   * destination itself, for an internal and an external recipient alike.
   *
   * The token travels in storage rather than in the address it sends them to: signing up takes a
   * person out of the app to confirm their email, and storage is what survives that round trip.
   *
   * Somebody already signed in goes into the app instead, never to Log in. The login page clears
   * the session it finds on the way past — it is written for a visitor who has none — and sending
   * a signed-in person through it logged them out of the account they were using, in every tab
   * they had open. `AuthGuard` picks the stored token up on arrival and accepts it, and answers a
   * mismatched account with the server's own refusal rather than with a silent sign-out.
   */
  useEffect(() => {
    if (!invite) return;

    dataRoomInviteStore.set({
      createdAt: Date.now(),
      email: invite.email,
      isAccepted: false,
      token: token ?? '',
    });

    if (accessTokenStorage.getState().get()) {
      navigate(PageRoute.HOME, { replace: true });

      return;
    }

    navigate(invite.hasAccount ? '/login' : '/register', { replace: true });
  }, [invite, navigate, token]);

  if (isLoading) {
    return (
      <Card>
        <Loader />
      </Card>
    );
  }

  if (error || !invite) {
    const { body, title } = refusalCopyOf(error?.body?.code);

    return (
      <Card>
        <h1 className="text-xl font-semibold text-gray-700">
          <AppFormattedMessage id={title} />
        </h1>
        {body && (
          <p className="text-sm font-[450] leading-5 text-gray-500">
            <AppFormattedMessage id={body} />
          </p>
        )}
      </Card>
    );
  }

  // Nothing of this page's own to show: the effect above is already on its way.
  return (
    <Card>
      <Loader />
    </Card>
  );
};
